import React, {
  createContext,
  useEffect,
  useState,
  useContext,
  useCallback,
} from "react";
import { apiAction } from "../axios";
import { PulseLoader } from "react-spinners";
import { APP_NAME, PUBLISHED_WEBSITE_DOMAIN } from "../app";
import { AccountContext } from "../account/account";
import { PLANS } from "../plans";
import { Helmet } from "react-helmet-async";
import { i18n } from "@lingui/core";

const WhiteLabelContext = createContext();

const SELF_DOMAINS = [
  "build.grigora.co",
  "localhost",
  "grigora-staging-app.pages.dev",
];

const WhiteLabelProvider = (props) => {
  const [isWhitelabel, setIsWhitelabel] = useState(false);
  const [whitelabelSettings, setWhitelabelSettings] = useState(null);
  const [whitelabelDomain, setWhitelabelDomain] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isWhiteLabelOwner, setIsWhiteLabelOwner] = useState(false);
  const [whitelabelPlans, setWhitelabelPlans] = useState({});
  const [currentLocale, setCurrentLocale] = useState(() => {
    return localStorage.getItem("locale") ? localStorage.getItem("locale") : "";
  });

  // Function to load the locale messages
  const loadLocaleMessages = async (locale) => {
    try {
      const messages = await import(`../../locales/${locale}/messages`);
      return messages.messages;
    } catch (error) {
      console.error(`Failed to load locale messages for ${locale}`, error);
      if (locale !== "en") {
        try {
          const fallbackMessages = await import(`../../locales/en/messages`);
          return (
            fallbackMessages.default ||
            fallbackMessages.messages ||
            fallbackMessages
          );
        } catch (fallbackError) {
          console.error("Failed to load fallback messages:", fallbackError);
          throw error;
        }
      }
      throw error;
    }
  };

  // Function to change the locale
  const changeLocale = async (locale) => {
    console.log("changing locale", locale, "current:", currentLocale);

    try {
      const messages = await loadLocaleMessages(locale);
      i18n.load(locale, messages);
      i18n.activate(locale);
      setCurrentLocale(locale);
    } catch (error) {
      console.error(`Failed to change locale to ${locale}`, error);
    }
  };

  async function getDomainSettings(currentDomain) {
    setIsLoading(true);

    try {
      const response = await apiAction(
        "POST",
        "general/user/whitelabel/settings",
        {
          domain: currentDomain,
        },
      );

      if (response?.Output?.data) {
        setWhitelabelSettings(response?.Output?.data);
        setIsWhitelabel(true);
        setWhitelabelDomain(currentDomain);

        // Load platform language
        let tempWhiteLabelSettings = response?.Output?.data;
        if (tempWhiteLabelSettings?.defaultLanguage) {
          const whitelabelLocale = tempWhiteLabelSettings.defaultLanguage;
          const userLocale = localStorage.getItem("locale");

          if (!userLocale) {
            changeLocale(whitelabelLocale);
          } else {
            changeLocale(userLocale);
          }
        }

        var newPlans = {};

        for (const plan of response?.Output?.data?.plans ?? []) {
          newPlans[plan?.planID] = plan;
        }

        // Add free plan for fallback
        newPlans["free"] = PLANS.free;

        setWhitelabelPlans(newPlans);
      }
    } catch (error) {
      console.error(error);
      // window.location.href = "https://" + SELF_DOMAINS?.[0];
    } finally {
      // Intentional timeout to ensure above set states are executed.
      setTimeout(() => {
        setIsLoading(false);
      }, 200);
    }
  }

  useEffect(() => {
    // Check the current domain
    const currentDomain = window.location.hostname;
    // const currentDomain = "www.yourdomain.com";

    if (!SELF_DOMAINS.includes(currentDomain)) {
      getDomainSettings(currentDomain);
    } else {
      setIsLoading(false);
      // Load default locales
      var userLocale = localStorage.getItem("locale");
      if (!userLocale) {
        changeLocale("en");
      } else {
        changeLocale(userLocale);
      }
    }
  }, []);

  const __whitelabel__ = (type, fallback) => {
    if (isWhitelabel) {
      return whitelabelSettings?.[type] || fallback;
    }

    return fallback;
  };

  const __whitelabel_website_publishing_domain__ = () => {
    if (isWhitelabel) {
      return "greatsite.app";
    }
    return PUBLISHED_WEBSITE_DOMAIN;
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <PulseLoader color="#000" size={6} />
      </div>
    );
  }

  return (
    <>
      {!isWhitelabel && (
        <Helmet>
          <title>{`${__whitelabel__("platformName", APP_NAME)}`}</title>
        </Helmet>
      )}
      <WhiteLabelContext.Provider
        value={{
          isWhitelabel,
          whitelabelSettings,
          whitelabelDomain,
          isWhiteLabelOwner,
          setIsWhiteLabelOwner,
          __whitelabel__,
          __whitelabel_website_publishing_domain__,
          whitelabelPlans,
          setWhitelabelPlans,
          currentLocale,
          loadLocaleMessages,
          changeLocale,
          setCurrentLocale,
        }}
      >
        {props.children}
      </WhiteLabelContext.Provider>
    </>
  );
};
export { WhiteLabelProvider, WhiteLabelContext };
